import { FC, Suspense, useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "./configs";
import { mainRoutes } from "./routes";
import { useRoutes } from "react-router-dom";
import { HmacSHA256, enc } from "crypto-js";
import "./assets/styles/rest.less";
import "./index.less";
import { message } from "antd";
import { getToken, getSignKey, reactConfig } from "./utils";
import { setupDevtoolsGuard } from "./utils/devtoolsGuard";
import { heantenant } from "./api";
import { HeantenantModel } from "./model";
import { useFetch } from "./hooks";

// 🔴 每次发版修改这个值
const APP_VERSION = "1.0.2"; // 发版时手动更新版本号

const BLANK_FAVICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";

const clearFavicon = () => {
  document
    .querySelectorAll(
      "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon'], link[rel='apple-touch-icon-precomposed']"
    )
    .forEach((node) => node.remove());

  const icon = document.createElement("link");
  icon.rel = "icon";
  icon.type = "image/svg+xml";
  icon.href = BLANK_FAVICON;
  document.head.appendChild(icon);
};

axios.defaults.baseURL = BASE_URL;

const App: FC<{}> = () => {
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const applyPageConfig = () => {
    clearFavicon();
    document.title = reactConfig().title;
  };

  // 非管理员禁用开发者工具（Rock 2026-09-16）。
  // 注意：这是提高门槛而非安全边界，真正的防线在后端不下发敏感数据。
  useEffect(() => {
    return setupDevtoolsGuard();
  }, []);

  // 🔴 版本检查逻辑：只在新版本发布时刷新一次
  useEffect(() => {
    const checkVersion = () => {
      const storedVersion = localStorage.getItem("app_version");

      if (storedVersion !== APP_VERSION) {
        console.log(`检测到新版本: ${storedVersion} -> ${APP_VERSION}`);
        setShouldRefresh(true);
        localStorage.setItem("app_version", APP_VERSION);
      }
    };

    checkVersion();
  }, []);

  // 🔴 如果检测到新版本，刷新页面
  useEffect(() => {
    if (shouldRefresh) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [shouldRefresh]);

  const { run: tenantDetailvoid } = useFetch<HeantenantModel["tenantDetail"]>(
    heantenant.TENANT_DETAIL,
    {},
    {
      manual: true,
      onResponse(res) {
        if (res.code === 200) {
          localStorage.setItem("tenant", JSON.stringify(res.data));
          applyPageConfig();
        } else {
          message.error({
            content: res.msg,
            className: "znrunpay-merorder-error",
          });
        }
      },
    }
  );

  useEffect(() => {
    applyPageConfig();

    if (!localStorage.getItem("tenant")) {
      tenantDetailvoid({
        method: "post",
        data: {
          teantWebsite: window.location.origin,
        },
      });
    }
  }, []);

  //请求拦截器
  axios.interceptors.request.use(
    (config) => {
      const { method } = config;
      // 前后端请求签名（安全事件 2026-09-16）：用登录时下发的会话级 signKey
      // 做 HMAC-SHA256(ts + "\n" + nonce)。signKey 不是常量、不在源码里、随会话失效，
      // 取代了原先埋在前端 JS 的固定 app_key（那把 key 公开且后端从不校验）。
      const ts = Math.floor(Date.now() / 1000);
      const nonce =
        (window.crypto && typeof window.crypto.randomUUID === "function"
          ? window.crypto.randomUUID()
          : `${ts}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
        ).replace(/-/g, "");
      const signKey = getSignKey();
      if (signKey) {
        const sign = HmacSHA256(`${ts}\n${nonce}`, signKey).toString(enc.Hex);
        config.headers["X-Req-Sign"] = sign;
        config.headers["X-Req-Ts"] = String(ts);
        config.headers["X-Req-Nonce"] = nonce;
      }
      config.headers["token"] = getToken();
      const authStorage = localStorage.getItem("authorization");
      if (authStorage) {
        config.headers["authorization"] = "Bearer " + authStorage;
      }
      config.headers["Content-Type"] =
        method === "put" ? "multipart/form-data" : config.headers["Content-Type"];
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    function (response) {
      if (response.data.code === 401) {
        message.error(response.data.msg);
        if (!window.location.pathname.startsWith("/doc/")) {
          window.top?.location.assign("/sign/login");
        }
      }
      return response;
    },
    function (error) {
      const url = error?.config?.url || "";
      // blob 下载失败时由业务页解析错误体，避免这里弹出空 toast
      const isBlob = error?.config?.responseType === "blob";
      if (!isBlob && !url.includes("/admin/draforder/createdraf")) {
        const errMsg =
          error.response?.data?.msg ||
          error.response?.data?.message ||
          (error.response?.status === 404 ? "接口不存在" : "") ||
          "请求失败，请稍后重试";
        message.error({
          content: errMsg,
          className: "login-message-error",
        });
      }
      return Promise.reject(error);
    }
  );

  return <Suspense fallback={""}>{useRoutes(mainRoutes)}</Suspense>;
};

export default App;
